import type { ReactNode } from 'react';
import { Platform } from 'react-native';
import {
  Box, HStack, Icon, Image, Pressable, VStack,
} from '@gluestack-ui/themed';
import { ChevronLeft } from 'lucide-react-native';

import { useAuthStore } from '@/common/auth/store';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import { UserMenu } from '@/common/navigation/components/UserMenu';

type MeetingChromeProps = {
  children: ReactNode;
  /** When provided, renders a back/leave affordance at the top of the rail. */
  onBack?: () => void;
};

/**
 * Lightweight app chrome for standalone meeting sessions: tenant logo and user
 * menu on a narrow rail, without full navigation icons. This is the single
 * logo-left bar for the meeting route (the stack header is hidden), so it also
 * hosts the back/leave affordance when `onBack` is supplied.
 */
export function MeetingChrome({ children, onBack }: MeetingChromeProps) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navbarIconUrl = useTenantSettingsStore((state) => state.settings?.imageAssetUrls?.navbarIconUrl);

  if (Platform.OS !== 'web') {
    return <Box flex={1}>{children}</Box>;
  }

  return (
    <HStack flex={1} bg="$bgApp" testID="meeting-chrome">
      <VStack
        bg="$bgApp"
        width={72}
        height="100%"
        alignItems="center"
        justifyContent="space-between"
        py={28}
        borderRightWidth={1}
        borderRightColor="$borderLight"
        testID="meeting-chrome-rail"
      >
        <VStack alignItems="center" space="md">
          {!!navbarIconUrl && (
            <Image
              source={{ uri: navbarIconUrl }}
              alt="Logo"
              testID="meeting-chrome-logo"
              width={38}
              height={38}
              resizeMode="contain"
              mb={16}
            />
          )}
          {onBack && (
            <Pressable
              onPress={onBack}
              testID="meeting-chrome-back"
              accessibilityRole="button"
              p="$2"
              borderRadius="$full"
              borderWidth={1}
              borderColor="$borderLight"
            >
              <Icon as={ChevronLeft} size="sm" color="$textHeading" />
            </Pressable>
          )}
        </VStack>
        <Box>
          <UserMenu user={user} logout={logout} placement="right bottom" />
        </Box>
      </VStack>
      <Box flex={1} overflow="hidden">
        {children}
      </Box>
    </HStack>
  );
}
